import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { InlineWarning } from '@driftt/tide-core'
import { access } from '@driftt/traffic-control'

import { CustomerApi, DeserializedSavedFilter } from 'api'
import { CurrentUserContext } from 'context/currentUserContext'

import styles from './styles.module.css'

const hasSeenNoNotificationsWarning = [
  {
    property: 'HAS_SEEN_NO_SAVED_VIEWS_NOTIFICATIONS_WARNING',
    operation: 'set',
    value: true
  }
]

const fetchCustomerProps = async (userId: number): Promise<void> => {
  return await CustomerApi.get(`/users/${userId}/properties`)
}

const patchProperties = async (userId: number): Promise<void> => {
  const resp = await CustomerApi.patch(
    `/users/${userId}/properties2`,
    hasSeenNoNotificationsWarning
  )
  return resp.data
}

interface InlineNotificationsWarningProps {
  savedFilters?: DeserializedSavedFilter[]
}

const InlineNotificationsWarning: React.FC<InlineNotificationsWarningProps> = ({
  savedFilters
}) => {
  const currentUser = useContext(CurrentUserContext)
  const [isOpen, setIsOpen] = useState(false)

  const hasSavedViewEmailDigest = access.hasGates('account_activity_email_digest')
  const hasSavedViewNotifications = access.hasGates('saved_filters_push_notifications')
  const hasCustomAccountActivityNotifs = hasSavedViewEmailDigest || hasSavedViewNotifications

  const onDismiss = () => {
    if (currentUser) {
      patchProperties(currentUser.id)
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (currentUser) {
      fetchCustomerProps(currentUser.id).then((res: any) => {
        const hasNotSeenWarningBefore = !res.data.HAS_SEEN_NO_SAVED_VIEWS_NOTIFICATIONS_WARNING
        const hasSavedFilters = Boolean(savedFilters && savedFilters.length > 0)
        const disabledAllNotifications =
          hasSavedFilters &&
          !savedFilters?.some((filter: any) => {
            return filter.hasDesktop || filter.hasMobile || filter.hasEmail
          })

        setIsOpen(
          hasCustomAccountActivityNotifs && disabledAllNotifications && hasNotSeenWarningBefore
        )
      })
    }
  }, [hasCustomAccountActivityNotifs, currentUser, savedFilters])

  if (!isOpen) {
    return null
  }

  return (
    <div>
      <InlineWarning
        className={styles.InlineCard}
        type="warning"
        title="You don’t have notifications turned on for any of your saved filters"
        buttonText="Got it"
        buttonType="secondary"
        onClick={onDismiss}
        copy={
          <span>
            This means you won’t receive push or email notifications when accounts visit your site.
            Choose which saved filters you’d like to be notified about in your{' '}
            <Link to="#notificationSettings">notification settings</Link>.
          </span>
        }
      />
    </div>
  )
}

export default InlineNotificationsWarning
